import type { MedplumClient } from '@medplum/core';
import type { Coding, Consent, Extension, Patient, Practitioner } from '@medplum/fhirtypes';

// ─── Extension URL constants ────────────────────────────────────────────────

export const NOTIFICATION_PREFERENCES_SYSTEM = 'http://polarissleep.com/notification-preferences';

export const PATIENT_EMAIL_ENABLED_URL = `${NOTIFICATION_PREFERENCES_SYSTEM}/email-enabled`;
export const PATIENT_SMS_ENABLED_URL = `${NOTIFICATION_PREFERENCES_SYSTEM}/sms-enabled`;

export const PROVIDER_NOTIFICATION_PREFERENCES_SYSTEM = 'http://polarissleep.com/provider-notification-preferences';

export const PROVIDER_APPOINTMENT_REMINDER_24H_URL = `${PROVIDER_NOTIFICATION_PREFERENCES_SYSTEM}/appointment-reminder-24h`;
export const PROVIDER_APPOINTMENT_REMINDER_15M_URL = `${PROVIDER_NOTIFICATION_PREFERENCES_SYSTEM}/appointment-reminder-15min`;
export const PROVIDER_RPM_REMINDER_ENABLED_URL = `${PROVIDER_NOTIFICATION_PREFERENCES_SYSTEM}/rpm-reminder-enabled`;

// ─── Preference types ───────────────────────────────────────────────────────

export interface PatientNotificationPreferences {
  emailEnabled: boolean;
  smsEnabled: boolean;
}

export interface ProviderNotificationPreferences {
  appointmentReminder24h: boolean;
  appointmentReminder15min: boolean;
  rpmReminderEnabled: boolean;
}

// ─── Defaults ───────────────────────────────────────────────────────────────

export const DEFAULT_PATIENT_PREFERENCES: PatientNotificationPreferences = {
  emailEnabled: true,
  smsEnabled: true,
};

export const DEFAULT_PROVIDER_PREFERENCES: ProviderNotificationPreferences = {
  appointmentReminder24h: true,
  appointmentReminder15min: true,
  rpmReminderEnabled: true,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function getBooleanExtension(extensions: Extension[] | undefined, url: string, defaultValue: boolean): boolean {
  const ext = extensions?.find((e) => e.url === url);
  return ext?.valueBoolean ?? defaultValue;
}

export function getPatientNotificationPreferences(patient: Patient): PatientNotificationPreferences {
  const prefExt = patient.extension?.find((e) => e.url === NOTIFICATION_PREFERENCES_SYSTEM);

  return {
    emailEnabled: getBooleanExtension(
      prefExt?.extension,
      PATIENT_EMAIL_ENABLED_URL,
      DEFAULT_PATIENT_PREFERENCES.emailEnabled
    ),
    smsEnabled: getBooleanExtension(
      prefExt?.extension,
      PATIENT_SMS_ENABLED_URL,
      DEFAULT_PATIENT_PREFERENCES.smsEnabled
    ),
  };
}

export function getProviderNotificationPreferences(practitioner: Practitioner): ProviderNotificationPreferences {
  const prefExt = practitioner.extension?.find((e) => e.url === PROVIDER_NOTIFICATION_PREFERENCES_SYSTEM);

  return {
    appointmentReminder24h: getBooleanExtension(
      prefExt?.extension,
      PROVIDER_APPOINTMENT_REMINDER_24H_URL,
      DEFAULT_PROVIDER_PREFERENCES.appointmentReminder24h
    ),
    appointmentReminder15min: getBooleanExtension(
      prefExt?.extension,
      PROVIDER_APPOINTMENT_REMINDER_15M_URL,
      DEFAULT_PROVIDER_PREFERENCES.appointmentReminder15min
    ),
    rpmReminderEnabled: getBooleanExtension(
      prefExt?.extension,
      PROVIDER_RPM_REMINDER_ENABLED_URL,
      DEFAULT_PROVIDER_PREFERENCES.rpmReminderEnabled
    ),
  };
}

/**
 * Build the FHIR extension structure for patient notification preferences.
 * Use this when saving preferences to a Patient resource.
 */
export function buildPatientPreferencesExtension(prefs: PatientNotificationPreferences): Extension {
  return {
    url: NOTIFICATION_PREFERENCES_SYSTEM,
    extension: [
      { url: PATIENT_EMAIL_ENABLED_URL, valueBoolean: prefs.emailEnabled },
      { url: PATIENT_SMS_ENABLED_URL, valueBoolean: prefs.smsEnabled },
    ],
  };
}

/**
 * Build the FHIR extension structure for provider notification preferences.
 * Use this when saving preferences to a Practitioner resource.
 */
export function buildProviderPreferencesExtension(prefs: ProviderNotificationPreferences): Extension {
  return {
    url: PROVIDER_NOTIFICATION_PREFERENCES_SYSTEM,
    extension: [
      { url: PROVIDER_APPOINTMENT_REMINDER_24H_URL, valueBoolean: prefs.appointmentReminder24h },
      { url: PROVIDER_APPOINTMENT_REMINDER_15M_URL, valueBoolean: prefs.appointmentReminder15min },
      { url: PROVIDER_RPM_REMINDER_ENABLED_URL, valueBoolean: prefs.rpmReminderEnabled },
    ],
  };
}

// ─── SMS Consent constants ──────────────────────────────────────────────────

export const SMS_CONSENT_POLICY_SYSTEM = 'http://polarissleep.com/consent-policy';
export const SMS_CONSENT_POLICY_CODE = 'tcpa-sms';

export const SMS_CONSENT_POLICY_CODING: Coding = {
  system: SMS_CONSENT_POLICY_SYSTEM,
  code: SMS_CONSENT_POLICY_CODE,
  display: 'TCPA SMS Messaging Consent (A2P 10DLC)',
};

/**
 * Search for the patient's currently-active SMS messaging Consent
 * (policyRule = tcpa-sms).
 *
 * Returns the most recent active Consent, or null when none exists.
 */
export async function searchActiveSmsConsent(medplum: MedplumClient, patientId: string): Promise<Consent | null> {
  const consents = (await medplum.searchResources('Consent', {
    patient: `Patient/${patientId}`,
    status: 'active',
    _sort: '-_lastUpdated',
    _count: 20,
  })) as Consent[];

  // Filter client-side for entries whose policyRule.coding matches both system and code
  // (Medplum doesn't natively support policyRule code search)
  const smsConsent = consents.find((consent) =>
    consent.policyRule?.coding?.some(
      (coding: Coding) => coding.code === SMS_CONSENT_POLICY_CODE && coding.system === SMS_CONSENT_POLICY_SYSTEM
    )
  );

  return smsConsent ?? null;
}
